/**
 * The header every level of the back-testing drill-down wears: a title, one quiet line
 * about it, and its actions on the right; levels below the first lead with the way back.
 * Shared so the levels cannot drift apart.
 */

import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface LevelHeaderProps {
	/** Omitted on the first level, which has nothing to go back to. */
	onBack?: () => void;
	backLabel?: string;
	title: ReactNode;
	subtitle?: ReactNode;
	actions?: ReactNode;
}

export const LevelHeader = ({ onBack, backLabel, title, subtitle, actions }: LevelHeaderProps) => (
	// Without a back button the title indents itself, to line up with the content it heads.
	<div className={cn('flex shrink-0 items-start gap-2 border-b border-gray-500 py-2 pr-1', onBack ? 'pl-1' : 'pl-4')}>
		{onBack && (
			<Button variant='ghost' size='icon' className='size-7 shrink-0' onClick={onBack} aria-label={backLabel}>
				<ArrowLeft className='size-4' />
			</Button>
		)}
		<div className='flex min-h-7 min-w-0 flex-1 flex-col justify-center'>
			{/* Divs, not paragraphs: callers pass elements, which a <p> may not contain.
			    Wraps rather than truncates: a scenario type is named by a whole sentence. */}
			<div className='text-[13px] leading-4.5 font-medium text-text-body'>{title}</div>
			{subtitle && <div className='text-xs leading-4 text-gray-800'>{subtitle}</div>}
		</div>
		{actions && <div className='flex shrink-0 items-center gap-1.5'>{actions}</div>}
	</div>
);
